import React from 'react';
import { clsx } from 'clsx';
import { Spinner } from '@/components/ui/Spinner';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { useBillingStatementDocument } from '../hooks/use-billing-statement-document';
import { CashPoolDocumentView } from './CashPoolDocumentView';
import { TradeIdLink } from '@/features/trades/components/TradeIdLink';
import { isCashPoolDocument } from '@/types';
import { NO_TRADE_ORIGIN } from '@/features/trades/trade-detail-search';
import type { BillingStatementListItem, StatementSectionKey } from '@/types';

// Short section labels for the Type cell; full titles live on the document
// page. Runtime fallback: the wire key is a plain string (see
// StatementSectionKey), so an unmapped section prints its key over nothing.
const SECTION_SHORT_LABELS: Record<StatementSectionKey, string> = {
  fees: 'Fee',
  cash_rebate: 'Rebate',
  cash_pool: 'Cash pool',
  repo_interest: 'Repo',
};

// `fees` holds both sides since LR-632 (the side is on the trade, not the
// section), so the Type cell takes it from the trade: a security lent and
// borrowed on one statement would otherwise print two identical "Fee" lines.
// 'Fee' above stays the fallback for a trade type this does not name.
const FEE_SIDE_LABELS: Record<string, string> = {
  lend: 'Lend fee',
  borrow: 'Borrow fee',
};

interface SecurityLine {
  key: string;
  /** Position PK — the Trades blotter's "ID", linkable to /trades/$tradeId (LR-613). */
  position_id: number;
  symbol_id: string;
  isin?: string;
  type: string;
  /** Daily lines behind `accrued`, from the group's own footing. */
  lines: number | null;
  /** The days those lines span: one date, or `first – last`. */
  period: string;
  /** The group's backend-computed subtotal — never summed on the FE (money). */
  accrued: string;
}

/**
 * Per-security slice under an expanded statement row (LR-557, mock's
 * "Per-security accruals" panel): one line per position per section, with the
 * section as its Type and the position's backend subtotal as Accrued.
 *
 * Lines / Period rather than the latest day's Qty and Rate (LR-571): the
 * document endpoint stopped sending daily rows - a statement covering a large
 * counterparty's month carries hundreds of thousands of them - so the last day's
 * snapshot is no longer in this payload, and printing an em dash for every
 * security would read as "no size on record". The daily figures live one click
 * away, on the document page, where a position expands into its own ledger.
 *
 * Data comes from the same document the detail page renders (one cache entry),
 * narrowed to the block of the expanded statement.
 */
export const BillingStatementExpandedRow: React.FC<{
  statement: BillingStatementListItem;
}> = ({ statement }) => {
  const { data, isLoading, error } = useBillingStatementDocument(statement.id);

  // LR-558: a cash pool statement has no securities to slice by — its lines are
  // daily pool balances. Render those instead of the per-security table, which
  // would otherwise report "No accrual lines" on a statement that plainly has a
  // total.
  //
  // Narrowed to THIS row's currency block: the document covers every pool the
  // counterparty holds, in every currency (product answer #15), and an expanded
  // USD row must not print the EUR statement underneath it.
  const fullCashPool = isCashPoolDocument(data) ? data : null;
  const cashPool = fullCashPool
    ? {
        ...fullCashPool,
        blocks: fullCashPool.blocks.filter(
          (b) => b.statement_id === statement.id,
        ),
      }
    : null;

  // `!= null`, not `!== undefined`: a JSON `null` body reaches callers as data
  // (see `isCashPoolDocument`), and it is neither shape - reading `.blocks` off
  // it throws.
  const block =
    data != null && !isCashPoolDocument(data)
      ? data.blocks.find((b) => b.statement_id === statement.id)
      : undefined;
  const lines: SecurityLine[] = (block?.sections ?? []).flatMap((section) =>
    section.groups.map((g) => ({
      key: `${section.key}-${g.position_id}`,
      position_id: g.position_id,
      symbol_id: g.symbol_id,
      isin: g.isin || undefined,
      type:
        (section.key === 'fees' ? FEE_SIDE_LABELS[g.trade_type] : undefined) ??
        SECTION_SHORT_LABELS[section.key] ??
        section.key,
      // Wire-boundary guard, not an HON-03 decimal one: these three are new in
      // LR-571 and the type says they are always there, but during a rolling
      // deploy this bundle can be served a document built by a backend pod that
      // has not rolled yet. Such a payload degrades to an em dash rather than
      // printing `undefined`. `StatementDocumentView` guards the same three for
      // the same reason - if one of them stops, both should.
      lines: g.row_count ?? null,
      period:
        g.first_date == null || g.last_date == null
          ? '—'
          : g.first_date === g.last_date
            ? g.first_date
            : `${g.first_date} – ${g.last_date}`,
      // The document's allocated cent (LR-632), so the lines add up to the
      // statement total the row above prints; the 8dp subtotal on an older
      // payload.
      accrued: g.amount ?? g.subtotal,
    })),
  );

  return (
    // Mock expansion panel: tinted, with a soft accent edge on the left.
    // The id is what the row's chevron references via aria-controls.
    <div
      id={`stmt-${statement.id}-securities`}
      className="border-b border-gray-200 border-l-[3px] border-l-[#dbe3f0] bg-[#fbfcfd] py-3 pl-[26px] pr-5"
    >
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.05em] text-gray-500">
        {cashPool ? 'Daily pool interest' : 'Per-security accruals'} ·{' '}
        {statement.counterparty_name} ·{' '}
        {statement.agreement_display || statement.statement_type_display} ·{' '}
        {statement.currency}
      </div>

      {isLoading && (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      )}
      {error != null && (
        <div className="text-sm text-red-600">
          Failed to load the per-security slice.
        </div>
      )}

      {cashPool && (
        <div className="max-w-[880px]">
          <CashPoolDocumentView document={cashPool} variant="compact" />
        </div>
      )}

      {!cashPool && !isLoading && error == null && lines.length === 0 && (
        <div className="text-sm text-gray-400">
          No accrual lines on this statement.
        </div>
      )}

      {lines.length > 0 && (
        <div className="max-w-[880px] overflow-x-auto rounded-[7px] border border-gray-200 bg-white">
          <table className="w-full table-fixed text-xs">
            <caption className="sr-only">
              Per-security accruals for {statement.counterparty_name} (
              {statement.currency})
            </caption>
            <colgroup>
              {/* Period holds `2026-06-01 – 2026-06-15`: 23 characters of
                  tabular digits, which does not fit 156px at 12px and would wrap
                  mid-range, taking the row height with it. Trade ID leads, as in
                  the blotter and the History drill-downs (LR-613). */}
              {['88px', '176px', '116px', '92px', '60px', '186px', '150px'].map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
            <thead className="bg-[#fafbfc]">
              <tr className="border-b border-gray-100">
                {[
                  ['Trade ID', 'left'],
                  ['Security', 'left'],
                  ['ISIN', 'left'],
                  ['Type', 'left'],
                  ['Lines', 'right'],
                  ['Period', 'left'],
                  ['Accrued', 'right'],
                ].map(([label, align]) => (
                  <th
                    key={label}
                    scope="col"
                    className={clsx(
                      'px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.03em] text-gray-400',
                      align === 'right' ? 'text-right' : 'text-left',
                      // Only the identifier. `table-fixed` makes a header that
                      // outgrows its column overlap its neighbour rather than
                      // wrap, and the tight ones have no px to spare (`LINES` is
                      // ~58px of a 60px column) - wrapping is the tamer failure
                      // there. `TRADE ID` is the one that has to read as a single
                      // label, and its 88px column was sized for it.
                      label === 'Trade ID' && 'whitespace-nowrap',
                    )}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                // Consumption-boundary guard (HON-03): typed string, may
                // arrive as a number at runtime.
                const accrued = toDecimalString(line.accrued) ?? '';
                const negative = accrued.startsWith('-');
                return (
                  <tr key={line.key} className="border-b border-gray-50 last:border-b-0">
                    {/* whitespace-nowrap, NOT truncate: an ellipsised identifier
                        ("104…") reads as a different trade (RunLineItems parity). */}
                    <td className="whitespace-nowrap px-3 py-1.5">
                      <TradeIdLink id={line.position_id} origin={NO_TRADE_ORIGIN} />
                    </td>
                    <td className="truncate px-3 py-1.5 font-medium text-gray-900">
                      {line.symbol_id}
                    </td>
                    <td className="truncate px-3 py-1.5 font-mono text-[11px] text-gray-500">
                      {line.isin ?? '—'}
                    </td>
                    <td className="px-3 py-1.5 text-gray-500">{line.type}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">
                      {line.lines == null ? '—' : line.lines.toLocaleString('en-US')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-gray-500">
                      {line.period}
                    </td>
                    <td
                      className={clsx(
                        'px-3 py-1.5 text-right font-medium tabular-nums',
                        negative ? 'text-red-600' : 'text-green-700',
                      )}
                    >
                      {formatDecimalString(line.accrued, {
                        currency: statement.currency,
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
